import React from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  capitalizeFirstLetter,
  formatLocalizedDateTime,
  Logger,
  useLocalize,
} from '@saviynt/common';
import {
  ApplicationIcon,
  ButtonIcon,
  Chip,
  Icon,
  Menu,
  Tooltip,
} from '@saviynt/design-system';

import CountdownTimer from '../components/CountdownTimer/CountdownTimer';
import { REQUEST_BASE_URL } from '../misc/constants';
import { selectedListDetails } from '../store/actions';
import { isAccount, isEndpoint, isRole, isSession } from '../utilities';
import { LISTROW_TYPES } from '../utilities/constants';

const useCreateRowObject = (
  row,
  isModalOpen,
  setIsModalOpen,
  setIconClickedForRow,
  setCheckInModalKind,
  listRowData,
  setListRowData,
  setIsExtendModalOpen,
  onLaunchSession,
  setIsSessionTargetModalVisible,
  setSessionDetailsForAd
) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const localize = useLocalize();
  const [{ userKey, user_name: userName }] = useCookies([
    'user_name',
    'userKey',
    'jitForApps',
  ]);
  const JIT = 'JIT';

  // JIT for Apps; domain config retrieved from redux
  const { PAM_JIT_FOR_APPS_ENABLED: isJitForApps } = useSelector(
    (state) => state.user.domainInfo || {}
  );

  const KINDS = {
    LABEL_VALUE: 'label-value',
    TEXT: 'text',
    SUBTLE_FILLED: 'subtleFilled',
    GHOST: 'ghost',
    SIMPLE: 'simple',
  };
  const SESSION_STATUSES = {
    SESSION_APPROVED: 1,
    SESSION_ACTIVE: 2,
  };
  const SESSION_STARTED =
    row?.sessionStatus === SESSION_STATUSES.SESSION_ACTIVE;

  const CREDENTIALLESS = 'CREDENTIALLESS';
  const CREDENTIAL = 'CREDENTIAL';
  const END_TYPE_CHECKIN = 'checkIn';
  const END_TYPE_END_SESSION = 'end';
  const END_TYPE_CANCEL_SESSION = 'cancelSession';
  const END_TYPE_CANCEL_REQUEST = 'cancelRequest';
  const EXTEND_SESSION = 'extendSession';
  const AD_RDP = 'adrdp';
  const IS_CREDENTIALLESS = row?.pamType?.toUpperCase() === CREDENTIALLESS;
  const IS_CREDENTIAL = row?.pamType?.toUpperCase() === CREDENTIAL;
  const EMERGENCY_ACCESS_ROLE = 'emergency access role';

  const isPasswordEnabled =
    row?.accountConfig?.['Saviynt-Status']?.showPassword === 'ENABLED';
  const isCredentialOrCredentialessWithPasswordEnabled =
    IS_CREDENTIAL || isPasswordEnabled;

  const msgs = {
    pamCheckInModal: {
      extendLabel: {
        id: 'pam.sidePanel.extend',
        defaultMessage: 'Extend',
      },
      connectButtonTooltip: {
        id: 'Pam.connect.tooltip',
        defaultMessage: 'Launch Session',
      },
      hasCredentialsTooltip: {
        id: 'pam.hasCredentialsTooltip.tooltip',
        defaultMessage: 'Has Credentials',
      },
      endButtonCredentiallessTooltip: {
        id: 'Pam.end.tooltip',
        defaultMessage: 'End Session',
      },
      endButtonCredentialedTooltip: {
        id: 'Pam.checkIn.tooltip',
        defaultMessage: 'Check In Credential',
      },
      checkInRoleTooltip: {
        id: 'pam.checkIn.tooltipRole',
        defaultMessage: 'Check In Role',
      },
      checkOutRoleTooltip: {
        id: 'pam.checkOut.tooltipRole',
        defaultMessage: 'Check Out Role',
      },
      checkOutAssetTooltip: {
        id: 'pam.checkOut.tooltipCredential',
        defaultMessage: 'Check Out Credential',
      },
      cancelRequestLabel: {
        id: 'pam.cancelRequest.title',
        defaultMessage: 'Cancel Request',
      },
      cancelSessionLabel: {
        id: 'pam.cancelSession.title',
        defaultMessage: 'Cancel Session',
      },
      pendingApproval: {
        id: 'pam.pendingApproval.title',
        defaultMessage: 'Pending Approval',
      },
      left: {
        id: 'pam.sidePanel.left',
        defaultMessage: 'left',
      },
    },
  };

  const isRdp = (remoteAppMetadata) =>
    remoteAppMetadata?.name?.toLowerCase() === AD_RDP;

  const createIcon = (type, kind, color, size) => {
    if (type === LISTROW_TYPES.ACCOUNT) {
      return <Icon kind='Account' color='neutral-1000' size='small' />;
    }

    return <Icon kind={kind} color={color} size={size} />;
  };

  const createButtonIcon = (
    actionMap,
    kind,
    icon,
    size,
    isDisabled,
    ariaLabel
  ) => (
    <ButtonIcon
      size={size}
      kind={kind}
      icon={icon}
      isDisabled={isDisabled}
      ariaLabel={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        actionMap();
      }}
    />
  );

  const getObjectForCategory = (account, platform, type) => {
    const icon = createIcon(type);

    const obj = {
      account,
      platform,
      icon,
    };

    return obj;
  };

  const getObjectForTitle = (endpointName, accountName, pamAccountType) => {
    const obj = [
      {
        kind: KINDS.TEXT,
        text: endpointName,
      },
    ];

    if (accountName) {
      obj.push({
        kind: KINDS.LABEL_VALUE,
        label: pamAccountType?.toUpperCase().includes(JIT)
          ? 'Account (JIT)'
          : 'Account',
        value: accountName,
      });
    }

    return obj;
  };

  const getPlatformType = (data) => {
    switch (data?.platformType?.toLowerCase()) {
      case 'aws':
      case 'gcp':
      case 'azure':
      case 'okta':
      case 'sap':
        return {
          kind: 'avatar-text',
          avatar: (
            <ApplicationIcon
              size='xxSmall'
              kind='logo'
              logo={data.platformType}
            />
          ),
          text: data.platformType,
        };
      case 'ad':
      case 'active directory':
        return {
          kind: 'avatar-text',
          avatar: <ApplicationIcon size='xxSmall' kind='logo' logo='AD' />,
          text: data.platformType,
        };
      default:
        return null;
    }
  };

  const buildRoleListRowDescription = (obj, data, showPlatformType) => {
    if (data?.roleType?.toLowerCase() === EMERGENCY_ACCESS_ROLE) {
      obj.push({
        kind: 'icon-text',
        icon: createIcon(LISTROW_TYPES.ROLE, LISTROW_TYPES.ROLE, null, 'small'),
        text: isJitForApps
          ? capitalizeFirstLetter(LISTROW_TYPES.ROLE)
          : data.roleType,
      });
    }

    // TODO: Enhance for cross-app after endpoints mapping is corrected by the backend
    if (showPlatformType) {
      const platformType = getPlatformType(data);

      if (platformType) {
        obj.push(platformType);
      }
    }

    if (data.roleDescription) {
      obj.push({ kind: KINDS.TEXT, text: data.roleDescription });
    }
  };

  const getObjectForDescription = (data) => {
    let obj = [];

    const isRoleListRow = isRole(data);

    if (isAccount(data)) {
      obj = [
        {
          kind: KINDS.TEXT,
          text: data.accountDescription,
        },
      ];
    } else if (isEndpoint(data) || isSession(data)) {
      if (isRoleListRow) {
        buildRoleListRowDescription(obj, data, true);
      } else {
        switch (data?.assetType?.toLowerCase()) {
          case 'server':
          case 'database':
            obj.push({
              kind: 'icon-text',
              icon: createIcon(data.type, data.assetType, null, 'small'),
              text: data.assetType,
            });
            break;
          case 'application':
            obj.push({
              kind: 'icon-text',
              icon: createIcon(data.type, 'Applications', null, 'small'),
              text: data.assetType,
            });
            break;
          default:
            break;
        }

        const platformType = getPlatformType(data);

        if (platformType) {
          obj.push(platformType);
        }

        if (
          data?.remoteAppMetadata?.displayName &&
          data?.remoteAppMetadata?.displayName?.toLowerCase() !== 'terminal'
        ) {
          obj.push({
            kind: KINDS.LABEL_VALUE,
            label: 'Remote App',
            value: data?.remoteAppMetadata?.displayName,
          });
        }

        if (data.epCustomproperty10) {
          obj.push({
            kind: KINDS.LABEL_VALUE,
            label: 'Location',
            value: data.epCustomproperty10,
          });
        }

        if (data.endpointDescription) {
          obj.push({ kind: KINDS.TEXT, text: data.endpointDescription });
        }
      }
    } else if (isRoleListRow) {
      buildRoleListRowDescription(obj, data, true);
    }

    return obj;
  };

  function getTitleObjectBasedOnType() {
    const isRoleListRow = isRole(row);

    if (isSession(row)) {
      const assetName = isRoleListRow
        ? row.roleDisplayName || row.roleName
        : row.endpointDisplayName || row.endpointName;

      return getObjectForTitle(assetName, row.accountName, row.pamAccountType);
    }

    if (isAccount(row)) {
      return [{ kind: KINDS.TEXT, text: row.accountName }];
    }

    if (isRoleListRow) {
      return [{ kind: KINDS.TEXT, text: row.roleDisplayName || row.roleName }];
    }

    return [
      { kind: KINDS.TEXT, text: row.endpointDisplayName || row.endpointName },
    ];
  }

  const createPrimaryIconOnClick = () => {
    const isSessionListRow = isSession(row);

    if (isJitForApps && isRole(row) && !isSessionListRow) {
      try {
        dispatch(selectedListDetails(row, 'success'));
      } catch (e) {
        Logger.error(e);
      } finally {
        history.push(`${REQUEST_BASE_URL}/check-out-role`);
      }
    } else if (isSessionListRow) {
      const sessionToBeLaunched = {
        userKey,
        userName,
        endDate: formatLocalizedDateTime(row.sessionEndDate),
        startDate: formatLocalizedDateTime(row.sessionStartDate),
        accountId: row.privilegeId,
        instanceId: row.instanceIdSession,
        status: row.sessionStatus,
        accessKey: row.requestAccessKey.toString(),
        hostIp: row.hostIp,
        osType: row.pamPlatformType,
        requestId: row.requestId,
        entitlementName: 'Instance',
        credentialtype: row.pamType,
        remoteApp: row.remoteApp,
        remoteAppMetadata: row.remoteAppMetadata,
      };

      if (isRdp(row.remoteAppMetadata)) {
        setIsSessionTargetModalVisible(true);
        setSessionDetailsForAd(sessionToBeLaunched);
      } else {
        onLaunchSession(sessionToBeLaunched);
      }
    }
  };

  const createSecondaryIconOnClick = () => {
    if (isEndpoint(row)) {
      history.push(`${REQUEST_BASE_URL}/check-out-credential`);
    } else if (isSession(row)) {
      setIsModalOpen(!isModalOpen);
      setIconClickedForRow(row);
      setCheckInModalKind(
        IS_CREDENTIALLESS ? END_TYPE_END_SESSION : END_TYPE_CHECKIN
      );
    }
  };

  function getPrimaryIconObject() {
    const isRoleListRow = isRole(row);
    const isSessionListRow = isSession(row);

    if (isJitForApps && isRoleListRow && !isSessionListRow) {
      const icon = createIcon(
        null,
        isRoleListRow ? 'CheckOut' : 'PasswordCredentialLocked',
        'navy-900',
        'medium'
      );

      return (
        <Tooltip
          text={
            isRoleListRow
              ? localize(msgs?.pamCheckInModal?.checkOutRoleTooltip)
              : localize(msgs?.pamCheckInModal?.checkOutAssetTooltip)
          }
          placement='left'
          trigger={createButtonIcon(
            createPrimaryIconOnClick,
            'outlinedSecondary',
            icon,
            'large',
            false,
            isRoleListRow
              ? localize(msgs?.pamCheckInModal?.checkOutRoleTooltip)
              : localize(msgs?.pamCheckInModal?.checkOutAssetTooltip)
          )}
        />
      );
    }

    const isEndpointCondition = isEndpoint(row);
    const isRoleWithoutSessionCondition = isRoleListRow && !isSessionListRow;

    if (isEndpointCondition || isRoleWithoutSessionCondition) {
      return createIcon(null, 'ArrowRight', 'navy-900', 'medium');
    }

    if (isSessionListRow && row?.pamType?.toUpperCase() === CREDENTIALLESS) {
      const icon = createIcon(null, 'LaunchRocket', 'neutral-000', 'medium');

      if (SESSION_STARTED) {
        return (
          <Tooltip
            text={localize(msgs?.pamCheckInModal?.connectButtonTooltip)}
            placement='left'
            trigger={createButtonIcon(
              createPrimaryIconOnClick,
              'bold',
              icon,
              'large',
              false,
              localize(msgs?.pamCheckInModal?.connectButtonTooltip)
            )}
          />
        );
      }

      return createButtonIcon(
        createPrimaryIconOnClick,
        'bold',
        icon,
        'large',
        true,
        localize(msgs?.pamCheckInModal?.connectButtonTooltip)
      );
    }

    return null;
  }

  function getSecondaryIconObject() {
    if (isEndpoint(row)) {
      return (
        <Tooltip
          text={localize(msgs?.pamCheckInModal?.connectButtonTooltip)}
          placement='left'
          trigger={createIcon(null, 'Connect', 'navy-700', 'medium')}
        />
      );
    }

    if (isSession(row)) {
      const icon = createIcon(
        null,
        'EndTerminateSubstract',
        'critical-700',
        'medium'
      );

      let endSessionTooltip =
        msgs?.pamCheckInModal?.endButtonCredentialedTooltip;

      if (isRole(row)) {
        endSessionTooltip = msgs?.pamCheckInModal?.checkInRoleTooltip;
      }

      if (row.pamType === CREDENTIALLESS) {
        endSessionTooltip =
          msgs?.pamCheckInModal?.endButtonCredentiallessTooltip;
      }

      if (SESSION_STARTED) {
        return (
          <Tooltip
            text={localize(endSessionTooltip)}
            placement='left'
            trigger={createButtonIcon(
              createSecondaryIconOnClick,
              'outlinedCritical',
              icon,
              'large',
              false,
              localize(endSessionTooltip)
            )}
          />
        );
      }

      return createButtonIcon(
        createSecondaryIconOnClick,
        'bold',
        icon,
        'large',
        true,
        localize(endSessionTooltip)
      );
    }

    return null;
  }

  function getCredentialIndicatorIcon() {
    if (SESSION_STARTED && isCredentialOrCredentialessWithPasswordEnabled) {
      return (
        <Tooltip
          text={localize(msgs.pamCheckInModal.hasCredentialsTooltip)}
          placement='left'
          trigger={
            <Icon
              kind='credentials'
              color='--color-foreground-info-medium'
              label={localize(msgs.pamCheckInModal.hasCredentialsTooltip)}
            />
          }
        />
      );
    }

    return null;
  }

  function getStatusObjectBasedOnType() {
    if (isSession(row)) {
      if (SESSION_STARTED) {
        return (
          <Chip
            kind={KINDS.SUBTLE_FILLED}
            color='brandSecondary'
            label={
              <CountdownTimer
                targetDate={row?.sessionEndDate}
                leftText={localize(msgs.pamCheckInModal.left)}
                onTimerExpired={() => {
                  // todo : add fade out transition
                  const updatedListRowData = { ...listRowData };

                  updatedListRowData.result.userAccesses =
                    updatedListRowData.result.userAccesses.filter(
                      (access) =>
                        access.requestAccessKey !== row.requestAccessKey
                    );
                  setListRowData(updatedListRowData);
                }}
              />
            }
            leftIcon={<Icon kind='Time' size='medium' />}
          />
        );
      }

      if (!row.sessionStatus) {
        return (
          <Chip
            kind={KINDS.SUBTLE_FILLED}
            color='info'
            label={localize(msgs.pamCheckInModal.pendingApproval)}
          />
        );
      }

      if (row.sessionStatus === SESSION_STATUSES.SESSION_APPROVED) {
        return (
          <Chip
            kind={KINDS.SUBTLE_FILLED}
            color='brandSecondary'
            label={formatLocalizedDateTime(row?.sessionStartDate, false, false)}
            leftIcon={<Icon kind='Calendar' size='medium' />}
          />
        );
      }
    }

    return null;
  }

  const optionsForActiveSessions = [
    {
      label: localize(msgs?.pamCheckInModal?.extendLabel),
      value: EXTEND_SESSION,
    },
  ];

  const optionsForFutureApprovedSession = [
    {
      label: localize(msgs?.pamCheckInModal?.cancelSessionLabel),
      value: END_TYPE_CANCEL_SESSION,
    },
  ];

  const optionsForPendingSessions = [
    {
      label: localize(msgs?.pamCheckInModal?.cancelRequestLabel),
      value: END_TYPE_CANCEL_REQUEST,
    },
  ];

  let options = [];

  if (!row.sessionStatus) {
    options = optionsForPendingSessions;
  } else if (row.sessionStatus === 1) {
    options = optionsForFutureApprovedSession;
  } else if (row.sessionStatus === 2) {
    options = optionsForActiveSessions;
  }

  function getMoreOptions() {
    const handleChange = (selectedOption) => {
      if (selectedOption.value === EXTEND_SESSION) {
        setIsExtendModalOpen(true);
        setIconClickedForRow(row);
      } else {
        setIsModalOpen(!isModalOpen);
        setIconClickedForRow(row);
        setCheckInModalKind(selectedOption.value);
      }
    };

    const trigger = (
      <ButtonIcon
        ariaLabel='More options'
        size='medium'
        kind={KINDS.GHOST}
        icon={<Icon kind='MoreVertical' color='neutral-100' size='medium' />}
      />
    );

    return (
      <Menu
        options={options}
        onChange={handleChange}
        trigger={trigger}
        isPortalEnabled
      />
    );
  }

  return () => ({
    category: isAccount(row)
      ? getObjectForCategory(row.accountName, row.platformType, row.type)
      : null,
    title: getTitleObjectBasedOnType(),
    description: getObjectForDescription(row),

    status: getStatusObjectBasedOnType(row),

    primaryIcon: getPrimaryIconObject(),
    secondaryIcon: getSecondaryIconObject(),
    moreOptionsIcon: isSession(row) ? getMoreOptions() : null,
    credentialIndicatorIcon: isSession(row)
      ? getCredentialIndicatorIcon()
      : null,
    type: row.type,
  });
};

export default useCreateRowObject;
